/**
 * Thin fetch wrapper used by TanStack Query hooks. Adds the Clerk session
 * token to every request, normalises error responses into Error instances
 * with the canonical error code attached, and routes through VITE_API_URL.
 *
 * Concrete query and mutation hooks live in `src/lib/queries/` and
 * `src/lib/mutations/` (added from M3 onward).
 */

import type { ApiError } from '@workspace/shared';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Bearer token, usually sourced from Clerk's `getToken()`. */
  token?: string;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, payload: ApiError) {
    super(payload.error.message);
    this.status = status;
    this.code = payload.error.code;
    this.details = payload.error.details;
  }
}

export async function apiRequest<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  const { body, token, headers, ...rest } = opts;
  const url = `${API_URL}${path}`;

  // Only send Content-Type when there is a JSON body. Fastify 4's
  // default JSON parser rejects an empty request body with
  // `Content-Type: application/json` as malformed, so endpoints like
  // POST /tasks/:id/complete (no body) used to 400 silently. Mutations
  // threw, onSuccess never fired, and the slide-over closed with no
  // feedback (UAT round 2: Complete, Frog, Mark Won, Mark Lost).
  const hasBody = body !== undefined;
  const res = await fetch(url, {
    ...rest,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers ?? {}),
    },
    body: hasBody ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let payload: ApiError;
    try {
      payload = (await res.json()) as ApiError;
    } catch {
      payload = {
        error: { code: 'unknown', message: `HTTP ${res.status}` },
      };
    }
    throw new ApiClientError(res.status, payload);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
